/// <amd-module name="Test/TestResultsGrid/Store/TestResultsTreeStore" />
import * as TestResultConverter from "Test/common-converters/TestResultConverter";
import {
    ColumnIndices,
    FeatureFlagConstants,
    FilterByFields,
    IViewContextData,
    TestCaseResultIdentifier,
    TestCaseResultIdentifierWithDuration,
    TestOutcomeEnum,
    TestResultFlakyState,
    TestResultsGroupPivots,
    TestResultsOutcomeFilterPivots,
    ViewContext,
    ViewContextStatus
} from "Test/common-util/Common";
import { TCMContractsConverter, TestReportDataParser } from "Test/common-util/Common.Utils";
import { FilterState } from "Test/common-util/Contracts/Filters";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { ITestResultTreeData, TreeNodeType } from "Test/common-util/Contracts/ViewContracts";
import { FilterHelper } from "Test/common-util/FilterHelper";
import { ShallowTestCaseResultData, TestResultsGridActionsHub } from "Test/TestResultsGrid/Actions/TestResultsGridActionHub";
import * as Resources from "Test/TestResultsGrid/Resources";
import { TestResultGridSource } from "Test/TestResultsGrid/Sources/TestResultsGridSource";
import { TestResultsFilteringManager } from "Test/TestResultsGrid/Store/FilteringManager";
import { TestResultsGroupByManager } from "Test/TestResultsGrid/Store/GroupByManager";
import { Constants, FilterSettings, GroupByPivotHelper } from "Test/TestResultsGrid/TestResultGridHelper";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Store } from "VSS/Reflux/Store";
import { ITreeItem, ITreeItemProvider, ITreeListItem } from "VSSUI/Tree";
import { ago } from "VSSUI/Utilities/Date";

declare type GetChildrenFunction = (item: ITestResultTreeData, uniqueId: number) => ITestResultTreeData[] | Promise<ITestResultTreeData[]>;
declare type GetTreeItemFunction = (item: ITestResultTreeData) => ITreeItem<ITestResultTreeData>;
class TestResultsTreeItemProvider implements ITreeItemProvider<ITestResultTreeData> {
    private _getChildren: GetChildrenFunction;
    private _getTreeItem: GetTreeItemFunction;
    private _map: Map<ITestResultTreeData, ITreeListItem<ITestResultTreeData>>;
    private _uniqueTreeId: number;

    constructor(
        getChildren: GetChildrenFunction,
        getTreeItem: GetTreeItemFunction,
        map: Map<ITestResultTreeData, ITreeListItem<ITestResultTreeData>>,
        uniqueTreeId: number
    ) {
        this._getChildren = getChildren;
        this._getTreeItem = getTreeItem;
        this._map = map;
        this._uniqueTreeId = uniqueTreeId;
    }

    public getChildren(item: ITestResultTreeData): ITestResultTreeData[] | Promise<ITestResultTreeData[]> {
        return this._getChildren(item, this._uniqueTreeId);
    }

    public getTreeItem(item: ITestResultTreeData): ITreeItem<ITestResultTreeData> {
        return this._getTreeItem(item);
    }

    public setTreeListItem(item: ITestResultTreeData, treeListItem: ITreeListItem<ITestResultTreeData>): void {
        treeListItem.key = item.key;
        this._map.set(item, treeListItem);
    }

    public getTreeListItem(item: ITestResultTreeData): ITreeListItem<ITestResultTreeData> {
        return this._map.get(item) || undefined as any;
    }

    public getTreeId(): number {
        return this._uniqueTreeId;
    }
}

export interface TestResultsTreeProviderOptions {
    pageContext: IVssPageContext;
    artifactData: IViewContextData;
    actionHub: TestResultsGridActionsHub;
}

export class TestResultsStore extends Store {
    private _pageContext: IVssPageContext;
    private _source: TestResultGridSource;
    private _artifactData: IViewContextData;
    private _state: ITestResultsState;
    private _treeListItemMap: Map<ITestResultTreeData, ITreeListItem<ITestResultTreeData>> = new Map(); // we can use Map in IE11 as long as we stick to simple functions like get/set

    private _groupsCache: ITestResultTreeData[] = []; // used to store list of groupNodes
    private _groupToResultsMap: { [key: string]: ITestResultTreeData[] } = {}; // Dictionary for groupId and TestResultGridRow
    private _subResultsCache: { [key: string]: TCMContracts.TestSubResult[] } = {}; // Dictionary of group ID to corresponding sub results.
    private _resultIdsToBeFetched: { [key: string]: TestCaseResultIdentifierWithDuration[] } = {}; // Dictionary of groupId and testResultIds for next page
    // Dictionary of groupId and testResultIds. This is needed to handle race condition
    // where we are removing ids from _resultIdsToBeFetched and before filling it in _groupToResultsMap, some other operation come and try to use the
    // concatenation of both _resultIdsToBeFetched and _groupToResultsMap.
    private _resultIdsToBeFetchedCached: { [key: string]: TestCaseResultIdentifierWithDuration[] } = {};
    private _groupIdOfRootLevelItems: string = "-1"; // used for groupby None
    private _isFirstBatchFetched: boolean = true;
    private _isAutoFetchComplete: boolean = false;
    private _groupByField: TestResultsGroupPivots;
    private _filterManager: TestResultsFilteringManager;
    private _groupByManager: TestResultsGroupByManager;
    private _treeItemProvider: TestResultsTreeItemProvider;
    private _uniqueRedrawGridId = new Date().getTime();
    private _tagsForTestRunFF: string = "TestManagement.Server.TagsInTestRun";

    constructor(options: TestResultsTreeProviderOptions) {
        super("");
        this._pageContext = options.pageContext;
        this._source = new TestResultGridSource(options.pageContext);
        this._artifactData = options.artifactData;
        this._state = this._getInitialState();

        options.actionHub.initializeTestResults.addListener(this._initializeGroups);
        options.actionHub.onError.addListener(this._onError);
        options.actionHub.onWarning.addListener(this._onWarning);
        options.actionHub.filterChanged.addListener(this._onFilterChanged);
        options.actionHub.groupedResultsFetched.addListener(this._cacheGroupedResultsAndUpdateCurrentState);
        options.actionHub.onGroupByChanged.addListener(this._onGroupByChanged);
        options.actionHub.shallowResultsFetched.addListener(this._cacheShallowResultsAndUpdateCurrentState);
        options.actionHub.onFetchMoreResults.addListener(this._onFetchMoreResults);
        options.actionHub.onSelectionChange.addListener(this._onSelectionChange);
        options.actionHub.onGridColumnChanged.addListener(this._updateGridColumn);
        options.actionHub.sortDataInGridAndToggleState.addListener(this._sortDataInGridAndToggleState);
        options.actionHub.onChangedToDefaultFilters.addListener(this._onDefaultFilterChanged);
        options.actionHub.onTestTagSummaryFetched.addListener(this._onTestTagSummaryFetched);
        options.actionHub.loadStageData.addListener(this._loadStageData);
        options.actionHub.onMarkTestResultFlaky.addListener(this._onMarkTestResultFlaky);
        options.actionHub.handleRunLevelResultCount.addListener(this._handleRunLevelResultCap);
        options.actionHub.handleRunSummaryCountMismatch.addListener(this._hideLoadingWhenNoRestCallsMade);

        this._filterManager = new TestResultsFilteringManager();
        this._groupByManager = new TestResultsGroupByManager();
        this._groupByField = TestResultsGroupPivots.Group_By_Test_Run;
    }

    public getState() {
        return this._state;
    }

    // TODO remove this method once dataprovider changes starts consuming stages
    public isStageCacheInitialized() {
        return this._filterManager.isStageCacheInitialized();
    }

    public getTreeItemProvider(): ITreeItemProvider<ITestResultTreeData> {
        if (!this._treeItemProvider || this._treeItemProvider.getTreeId() !== this._uniqueRedrawGridId) {
            this._treeItemProvider = new TestResultsTreeItemProvider(
                this._getChildren,
                this._getTreeItem,
                this._treeListItemMap,
                this._uniqueRedrawGridId
            );
        }

        return this._treeItemProvider;
    }

    public onFetchMoreElements(groupId: string) {
        const idsToBeFetched = this._getNextResultsToBeFetched(groupId);
        if (idsToBeFetched.length === 0) {
            return;
        }
        this._source.getTestResultsByQueryForGivenResultIdentifiers(idsToBeFetched).then(response => {
            if (!response || !response.results) {
                return [];
            }
            this._addResultsIntoTestRunToResultsMap(response.results, groupId);
            this._isFirstBatchFetched = true;
            this._state.showingResultsCount = this._state.fetchedResultsCount;
            this._uniqueRedrawGridId = new Date().getTime();
            this.emitChanged();
        });
    }

    private _getInitialState() {
        return {
            columnToRender: this._initiallyRenderingColumnList(),
            loading: true,
            endPerfMarker: false,
            totalResultsCount: 0,
            fetchedResultsCount: 0,
            showingResultsCount: 0,
            failedAbortedCount: 0,
            isInitialized: false,
            viewContext: this._artifactData.viewContext,
            filterState: FilterHelper.getInitialFilterState(),
            groupByField: TestResultsGroupPivots.Group_By_Test_Run,
            resultsFound: false,
            warningMessage: null,
            errorMessage: null,
            isFilteredOrGrouped: false,
            isSortedDescending: true,
            hasMegaFetchStarted: false,
            showLoading: true
        } as ITestResultsState;
    }

    private _onSelectionChange = (selectedItems: ITestResultTreeData[]) => {
        this._state.selectedTreeItems = selectedItems;
        this.emitChanged();
    };

    private _onFetchMoreResults = () => {
        if (this._state.showingResultsCount < this._state.fetchedResultsCount) {
            this._updateResultsAndCurrentState(true);
        }
        this._isFirstBatchFetched = true;
        this._uniqueRedrawGridId = new Date().getTime();
    };

    private _initializeGroups = (response: TCMContracts.TestResultsDetails) => {
        let totalTestsCount = 0;
        let failedAbortedCount = 0;
        this._state.loading = false;
        this._state.isInitialized = true;
        this._uniqueRedrawGridId = new Date().getTime();
        if (!response || response.resultsForGroup.length === 0) {
            this._state.endPerfMarker = true; // Marking the perf as complete when there are no tests also.
            this.emitChanged();
            return;
        }

        response.resultsForGroup.forEach(groupSummary => {
            if (groupSummary.resultsCountByOutcome) {
                for (const outcome in groupSummary.resultsCountByOutcome) {
                    totalTestsCount += groupSummary.resultsCountByOutcome[outcome].count;
                }
            }

            failedAbortedCount += groupSummary.results ? groupSummary.results.length : 0;
        });
        this._state.failedAbortedCount = failedAbortedCount;
        this._state.totalResultsCount = totalTestsCount;
        // caching the grouped Results
        this._groupByManager.populateGroupedResults(response);

        if (this._state.isFilteredOrGrouped) {
            this._onFilterChanged(this._state.filterState);
        } else {
            this._initializeGroupDetails(response);
            this._expandFirstGroupBy();
        }
        this._state.endPerfMarker = true;
        this._state.errorMessage = null;
        this._state.warningMessage = null;
        this.emitChanged();
    };

    private _loadStageData = (resultGroup: TCMContracts.TestResultsDetails) => {
        this._filterManager.populateStageCache(resultGroup);
    };

    private _onDefaultFilterChanged = (filterState: FilterState) => {
        this._state.filterState = filterState;
        this._state.loading = true;
        this.emitChanged();
    };

    private _onTestTagSummaryFetched = (testTagSummary: TCMContracts.TestTagSummary) => {
        this._groupByManager.updateTagsForTestRun(testTagSummary);
        if (this._groupByField === TestResultsGroupPivots.Group_By_Test_Run && this._groupsCache && this._groupsCache.length > 0) {
            this._groupsCache.forEach(element => {
                const runId = element.runId;
                if (runId > 0) {
                    if (!!testTagSummary.tagsGroupByTestArtifact[runId]) {
                        element.tags = testTagSummary.tagsGroupByTestArtifact[runId].map(tag => tag.name);
                    }
                }
            });
            this.emitChanged();
        }
    };

    private _onError = (errorMessage: string) => {
        this._state.loading = false;
        this._state.errorMessage = errorMessage;
        this._state.endPerfMarker = true;
        this.emitChanged();
    };

    private _onWarning = (warningMessage: string) => {
        this._state.warningMessage = warningMessage;
        this.emitChanged();
    }
    private _cacheGroupedResultsAndUpdateCurrentState = (groupedResults: TCMContracts.TestResultsDetails) => {
        this._groupByManager.populateGroupedResults(groupedResults);

        this._updateResultsAndCurrentState(true);
    };

    private _onGroupByChanged = (groupByField: TestResultsGroupPivots) => {
        this._groupByField = groupByField;
        this._state.groupByField = groupByField;
        this._state.isFilteredOrGrouped = true;
        this._state.loading = true;
        this._updateResultsAndCurrentState();

        // allowing the first batch to reflect in UI
        this._isFirstBatchFetched = true;
        this._handleShallowFetch();
        this.emitChanged();
    };

    private _onFilterChanged = (filterState: FilterState) => {
        this._state.filterState = filterState;
        this._state.isFilteredOrGrouped = true;
        this._state.loading = true;
        this._isFirstBatchFetched = true;
        this._updateResultsAndCurrentState();
        this._handleShallowFetch();
        this.emitChanged();
    };

    private _updateGridColumn = (updatedColumn: string[]) => {
        this._state.columnToRender = updatedColumn;
        this.emitChanged();
    };

    private _cacheShallowResultsAndUpdateCurrentState = (shallowResultsData: ShallowTestCaseResultData) => {
        this._cacheShallowResults(shallowResultsData.shallowResults);
        const isFetchAllCompleted: boolean = isFeatureFlagEnabled(
            this._pageContext,
            FeatureFlagConstants.EnableFetchAllResultsUntilMegaBatchSize,
            false
        )
            ? this._isAutoFetchComplete
            : false;
        if (this._isFirstBatchFetched || isFetchAllCompleted) {
            // Make loading false when there is no results from server for the applied filter.
            // There can be a scenario where results is empty because fetching of results is already going on and in this case dont remove spinner(loading = false).
            // The spinner will get removed when data will come from server.
            if (
                !shallowResultsData ||
                ((!shallowResultsData.shallowResults || shallowResultsData.shallowResults.length == 0) &&
                    shallowResultsData.isResultFetchedFromServer == true)
            ) {
                this._state.loading = false;
                this.emitChanged();
            } else {
                this._updateResultsAndCurrentState(true);
            }
            this._isFirstBatchFetched = false;
            this._isAutoFetchComplete = false;
        } else {
            this.emitChanged();
        }
    };

    private _sortDataInGridAndToggleState = () => {
        this._state.isSortedDescending = !this._state.isSortedDescending;
        if (!this._state.isFilteredOrGrouped) {
            this._sortUnfilterdData();
        } else {
            this._updateResultsToRender();
        }
        this._expandFirstGroupBy();
        this._uniqueRedrawGridId = new Date().getTime();
        this.emitChanged();
    };

    private _expandFirstGroupBy(): void {
        // expand first group By
        if (this._groupsCache && this._groupsCache.length > 0) {
            this._groupsCache[0].expanded = true;
        }
    }

    private _sortUnfilterdData() {
        for (const key in this._groupsCache) {
            const concatenatedResults: TestCaseResultIdentifierWithDuration[] = this._resultIdsToBeFetchedCached[key]
                ? this._resultIdsToBeFetchedCached[key]
                : [];
            if (this._state.isSortedDescending) {
                this._resultIdsToBeFetched[key] = concatenatedResults.sort(TestResultConverter.sortingIdentifierDescending);
            } else {
                this._resultIdsToBeFetched[key] = concatenatedResults.sort(TestResultConverter.sortingIdentifierAscending);
            }
            // Resetting the cache so that there are no duplications as the data from this cache has been sorted and stored into
            // cache _resultIdsToBeFetched for further processing
            delete this._groupToResultsMap[key];
        }

        this._sortGroupsCache();
    }

    private _onMarkTestResultFlaky = (): void => {
        // Flaky Test Result State is being updated outside the store, ITestResultTreeData is being passed as reference and hence it works.
        this.emitChanged();
    };

    private _cacheShallowResults(resultsShallowDetails: TCMContracts.ShallowTestCaseResult[]) {
        if (resultsShallowDetails && resultsShallowDetails.length > 0) {
            resultsShallowDetails.forEach(result => {
                this._filterManager.populateCache(result);
                this._groupByManager.populateShallowResult(result);
            });

            this._state.fetchedResultsCount += resultsShallowDetails.length;
            const isFetchAllCompleted: boolean =
                this._state.fetchedResultsCount < FilterSettings.getMegaResultsSize(this._pageContext)
                    ? this._state.hasMegaFetchStarted
                        ? this._state.fetchedResultsCount >= this._state.totalResultsCount
                        : this._state.fetchedResultsCount === this._state.failedAbortedCount
                    : false;
            const shallowResultsBatchSize: number = isFeatureFlagEnabled(
                this._pageContext,
                FeatureFlagConstants.EnableFetchAllResultsUntilMegaBatchSize,
                false
            )
                ? FilterSettings.getNewShallowResultsBatchSize(this._pageContext)
                : Constants.shallowResultsBatchSize;
            if (
                (this._state.fetchedResultsCount >= FilterSettings.getMegaResultsSize(this._pageContext) &&
                    this._state.fetchedResultsCount < FilterSettings.getMegaResultsSize(this._pageContext) + shallowResultsBatchSize) ||
                isFetchAllCompleted
            ) {
                this._isAutoFetchComplete = true;
            }
        }
    }

    private _updateResultsAndCurrentState(shouldEmitChange: boolean = false) {
        if (this._groupByManager.isGroupBySummaryLoaded(this._groupByField) && this._state.fetchedResultsCount > 0) {
            this._updateResultsToRender();
            this._uniqueRedrawGridId = new Date().getTime();
            if (shouldEmitChange) {
                this.emitChanged();
            }
        }
    }

    private _updateResultsToRender() {
        const groupedResults = this._groupByManager.getGroupByResults(this._groupByField);
        const filteredGroupedResults = this._filterManager.filterGroupedResults(groupedResults, this._state.filterState);

        this._initializeGroupDetails(filteredGroupedResults);
        // since fetchedResultsCount will be shown now hence assigning it to showingResultsCount
        this._state.showingResultsCount = this._state.fetchedResultsCount;
    }

    private _initiallyRenderingColumnList(): string[] {
        const columnIndices: string[] = [];
        columnIndices.push(ColumnIndices.Duration);
        columnIndices.push(ColumnIndices.FailingSince);
        if (this._artifactData.viewContext === ViewContext.Build) {
            columnIndices.push(ColumnIndices.FailingBuild);
        } else if (this._artifactData.viewContext === ViewContext.Release) {
            columnIndices.push(ColumnIndices.FailingRelease);
        }

        if (isFeatureFlagEnabled(this._pageContext, this._tagsForTestRunFF, false)) {
            columnIndices.push(ColumnIndices.Tags);
        }

        return columnIndices;
    }

    private _getChildren: GetChildrenFunction = (item: ITestResultTreeData, uniqueId: number) => {
        if (uniqueId !== this._uniqueRedrawGridId) { return []; }

        if (!item) {
            if (this._groupsCache && this._groupsCache.length > 0) {
                this._expandFirstGroupBy();
                return this._groupsCache;
            }
        }

        // In case of rerun and orderBy tests subresults will be present
        if (item && item.nodeType === TreeNodeType.group && item.isTestCaseRow) {
            if (this._groupToResultsMap[item.groupId]) {
                return this._groupToResultsMap[item.groupId];
            }

            return this._source.getSelectedTestCaseResult(this._artifactData, item.runId, item.resultId ? item.resultId : 0).then(response => {
                if (!response) {
                    return [];
                }

                this._addSubResultsIntoGroupToResultsMap(response, item.groupId, item.isSubResultRow);
                return this._groupToResultsMap[item.groupId];
            });
        }

        // In case of group By none item will be undefined and we need to put -1 as groupId.
        const currentGroupId = item ? item.groupId : this._groupIdOfRootLevelItems;
        if (this._groupToResultsMap[currentGroupId]) {
            if (this._resultIdsToBeFetched[currentGroupId] && this._resultIdsToBeFetched[currentGroupId].length > 0) {
                const showMoreElement: ITestResultTreeData = {
                    nodeType: TreeNodeType.showMore,
                    groupId: currentGroupId,
                    expanded: false,
                    runId: 0,
                    isTestCaseRow: true,
                    key: this._generateKey(this._resultIdsToBeFetched[currentGroupId])
                };
                return this._groupToResultsMap[currentGroupId].concat([showMoreElement]);
            }
            return this._groupToResultsMap[currentGroupId];
        }

        // Code to be executed when show more is invoked
        const idsToBeFetched = this._getNextResultsToBeFetched(currentGroupId);
        if (idsToBeFetched.length === 0) {
            return [];
        }

        return this._source.getTestResultsByQueryForGivenResultIdentifiers(idsToBeFetched).then(response => {
            if (!response || !response.results || uniqueId !== this._uniqueRedrawGridId) {
                return [];
            }
            this._addResultsIntoTestRunToResultsMap(response.results, currentGroupId);
            if (this._resultIdsToBeFetched[currentGroupId] && this._resultIdsToBeFetched[currentGroupId].length > 0) {
                const showMoreElement: ITestResultTreeData = {
                    nodeType: TreeNodeType.showMore,
                    groupId: currentGroupId,
                    expanded: false,
                    runId: 0,
                    isTestCaseRow: true,
                    key: this._generateKey(this._resultIdsToBeFetched[currentGroupId])
                };
                return this._groupToResultsMap[currentGroupId].concat([showMoreElement]);
            }
            return this._groupToResultsMap[currentGroupId];
        });
    };

    private _addSubResultsIntoGroupToResultsMap(results: TCMContracts.TestCaseResult, parentGroupId: string, isSubResults: boolean | undefined) {
        let subResults: TCMContracts.TestSubResult[] | undefined = [];
        if (isSubResults) {
            subResults = this._subResultsCache[parentGroupId];
        } else {
            subResults = results.subResults;
        }
        if (!subResults) {
            return;
        }

        if (subResults) {
            // Ordering from latest to oldest subresults
            for (let i = subResults.length - 1; i >= 0; --i) {
                const testCaseResult = results,
                    subResult = subResults[i];
                const resultViewModel = this._getResultViewModelFromSubresult(testCaseResult, parentGroupId, subResult);
                if (!this._groupToResultsMap[parentGroupId]) {
                    this._groupToResultsMap[parentGroupId] = [];
                }
                // insert results corresponding to the given group id.
                this._groupToResultsMap[parentGroupId].push(resultViewModel);

                const groupId = testCaseResult.testRun.id.toString() + "." + testCaseResult.id.toString() + "." + subResult.id.toString();
                if (resultViewModel.subresults) {
                    this._subResultsCache[groupId] = resultViewModel.subresults;
                }
            }
        }
    }

    private _addResultsIntoTestRunToResultsMap(results: TCMContracts.TestCaseResult[], groupId: string) {
        results.map(result => {
            const resultViewModel = this._getResultViewModelFromTestResult(result, groupId);

            if (!this._groupToResultsMap[groupId]) {
                this._groupToResultsMap[groupId] = [];
            }
            // insert results corresponding to the given group id.
            this._groupToResultsMap[groupId].push(resultViewModel);

            // sort in same order as current order for more results that are being loaded
            if (this._state.isSortedDescending) {
                this._groupToResultsMap[groupId].sort((a, b) => {
                    return TestResultConverter.sortDuration(a.durationInMs as number, b.durationInMs as number, false);
                });
            } else {
                this._groupToResultsMap[groupId].sort((a, b) => {
                    return TestResultConverter.sortDuration(a.durationInMs as number, b.durationInMs as number, true);
                });
            }
        });
    }

    private _getResultViewModelFromSubresult(
        testCaseResult: TCMContracts.TestCaseResult,
        groupId: string,
        subResult: TCMContracts.TestSubResult
    ): ITestResultTreeData {
        let contextId = 0;
        let contextName = "";
        const context = this._artifactData;
        let isCurrentArtifact = false;
        if (testCaseResult.failingSince && context) {
            switch (context.viewContext) {
                case ViewContext.Build:
                    contextId = testCaseResult.failingSince.build ? testCaseResult.failingSince.build.id : 0;
                    contextName = TestResultConverter.getFailingSinceBuildString(context.data.mainData.id, testCaseResult);
                    isCurrentArtifact = context.data.mainData.id === testCaseResult.failingSince.build.id;
                    break;
                case ViewContext.Release:
                    contextId = testCaseResult.failingSince.release ? testCaseResult.failingSince.release.id : 0;
                    contextName = TestResultConverter.getFailingSinceReleaseString(context.data.mainData.id, testCaseResult);
                    isCurrentArtifact = context.data.mainData.id === testCaseResult.failingSince.release.id;
                    break;
            }
        }

        const resultViewModel: ITestResultTreeData = {
            test: subResult.displayName ? subResult.displayName : format(Resources.SubResultDisplayName, subResult.id.toString()),
            testTitle: subResult.displayName,
            storage: testCaseResult.automatedTestStorage || "",
            failingSince: testCaseResult.failingSince ? ago(testCaseResult.failingSince.date) : "",
            failingContextName: contextName,
            isNewFailure: false,
            runId: parseInt(testCaseResult.testRun.id),
            resultId: testCaseResult.id,
            testcaseObject: testCaseResult.testCase,
            testCaseRefId: testCaseResult.testCaseReferenceId,
            failingContextId: contextId,
            isTestCaseRow: true,
            outcome: (TestOutcomeEnum as any)[subResult.outcome],
            duration: subResult.durationInMs
                ? TCMContractsConverter.ConvertMilliSecondsToReadableFormatForResultSummary(subResult.durationInMs)
                : TCMContractsConverter.ConvertMilliSecondsToReadableFormatForResultSummary(0),
            dateStarted: subResult.startedDate ? subResult.startedDate.toLocaleDateString() : "",
            dateCompleted: subResult.completedDate ? subResult.completedDate.toLocaleDateString() : "",
            owner: testCaseResult.owner ? testCaseResult.owner.displayName : "",
            nodeType:
                subResult.resultGroupType != null && subResult.resultGroupType !== TCMContracts.ResultGroupType.None
                    ? TreeNodeType.group
                    : TreeNodeType.leaf,
            parentGroupId: groupId,
            subresults: subResult.subResults,
            groupId: testCaseResult.testRun.id.toString() + "." + testCaseResult.id.toString() + "." + subResult.id.toString(),
            subResultId: subResult.id,
            isSubResultRow: true,
            isCurrentArtifact: isCurrentArtifact,

            // [TODO]: For build scenario it is not needed.
            environmentName: testCaseResult.releaseReference ? TestResultConverter.getEnvironmentName(context) : ""
        };

        return resultViewModel;
    }

    private _generateKey(resultIds: TestCaseResultIdentifierWithDuration[]): string {
        let uniqueKey: string = resultIds[0].toString();
        const currentSize = resultIds.length;

        if (currentSize > Constants.pageSize) {
            uniqueKey = uniqueKey.concat(resultIds[Constants.pageSize - 1].toString());
        } else {
            uniqueKey = uniqueKey.concat(resultIds[currentSize - 1].toString());
        }

        // making the key unique since in some scenarios loading gets stuck as react does not call RowDidMount as already mounted if key is same
        return uniqueKey.concat("showMore" + new Date().getTime());
    }

    private _getResultViewModelFromTestResult(testCaseResult: TCMContracts.TestCaseResult, groupId: string): ITestResultTreeData {
        const resultViewModel = TestResultConverter.getResultViewModelFromTestResult(testCaseResult, this._artifactData, groupId);
        resultViewModel.isUnreliable = this._isTestUnreliable(testCaseResult.customFields);
        resultViewModel.flakyState = this._getFlakyState(testCaseResult.customFields);
        return resultViewModel;
    }

    private _initializeGroupDetails(groupedResults: TCMContracts.TestResultsDetails) {
        const groupBy = groupedResults.groupByField;

        this._resultIdsToBeFetched = {};
        this._resultIdsToBeFetchedCached = {};
        this._groupToResultsMap = {};
        this._groupsCache = [];
        this._state.resultsFound = false;
        this._state.loading = false;

        groupedResults.resultsForGroup.map((resultGroup, index) => {
            if (this._shouldShowGroupDetails(resultGroup, this._state.filterState)) {
                let groupId = this._groupIdOfRootLevelItems;
                const currentGroupId = index.toString();
                if (groupBy) {
                    const groupObject = this._getTestGroupDetails(resultGroup, groupBy as TestResultsGroupPivots, currentGroupId);

                    // initialize _groupsCache to get group information.
                    this._groupsCache.push(groupObject);
                    groupId = currentGroupId;
                }

                let sortedData: TCMContracts.TestCaseResult[] = [];
                if (this._state.isSortedDescending) {
                    sortedData = resultGroup.results.sort(TestResultConverter.sortingComparatorDescending);
                } else {
                    sortedData = resultGroup.results.sort(TestResultConverter.sortingComparatorAscending);
                }

                sortedData.map(result => {
                    const identifier = new TestCaseResultIdentifierWithDuration(parseInt(result.testRun.id), result.id, result.durationInMs);

                    // inserting results which are yet to be fetched from server.
                    if (!this._resultIdsToBeFetched[groupId]) {
                        this._resultIdsToBeFetched[groupId] = [];
                        this._resultIdsToBeFetchedCached[groupId] = [];
                    }
                    this._resultIdsToBeFetched[groupId].push(identifier);
                    this._resultIdsToBeFetchedCached[groupId].push(identifier);
                });

                this._state.resultsFound = true;
            }
        });

        this._sortGroupsCache();
    }

    private _sortGroupsCache(): void {
        if (this._state.isSortedDescending) {
            this._groupsCache = this._groupsCache.sort(TestResultConverter.sortingTestTreeDataComparerDescending);
        } else {
            this._groupsCache = this._groupsCache.sort(TestResultConverter.sortingTestTreeDataComparerAscending);
        }
    }

    private _shouldShowGroupDetails(resultsForGroup: TCMContracts.TestResultsDetailsForGroup, filterState: FilterState): boolean {
        if (resultsForGroup.results.length > 0) {
            return true;
        }

        const inProgressRunsFlag = "WebAccess.TestManagement.ShowInProgressRunsInInProgressDefaultView";
        if (
            isFeatureFlagEnabled(this._pageContext, inProgressRunsFlag, false) &&
            this._artifactData.status === ViewContextStatus.InProgress &&
            resultsForGroup.groupByValue.state === TestOutcomeEnum[TestOutcomeEnum.InProgress]
        ) {
            return true;
        }

        return this._shouldShowAbortedRuns(resultsForGroup, filterState);
    }

    private _shouldShowAbortedRuns(resultsForGroup: TCMContracts.TestResultsDetailsForGroup, filterState: FilterState): boolean {
        const isRunStateAborted = resultsForGroup.groupByValue.state
            ? resultsForGroup.groupByValue.state === TestResultsOutcomeFilterPivots.Filter_By_Aborted
            : false;

        const filterString = FilterHelper.generateFilterString(filterState);
        const isOutcomeFilterAborted = filterString.indexOf(TestResultsOutcomeFilterPivots.Filter_By_Aborted) > 0;

        if (isRunStateAborted && (isOutcomeFilterAborted || equals(filterString, "")) && FilterHelper.isTagFilterEnabled(filterState) === false) {
            return true;
        }
        return false;
    }

    private _getNextResultsToBeFetched(groupId: string): TestCaseResultIdentifier[] {
        if (!this._resultIdsToBeFetched[groupId]) {
            return [];
        }

        const currentSize = this._resultIdsToBeFetched[groupId].length;
        if (currentSize > Constants.pageSize) {
            const idsToBeFetched = this._resultIdsToBeFetched[groupId].splice(0, Constants.pageSize - 1);
            return idsToBeFetched;
        }
        const idsToBeFetched = this._resultIdsToBeFetched[groupId];
        delete this._resultIdsToBeFetched[groupId];

        return idsToBeFetched;
    }

    /// <summary>
    /// Calculates the run outcome based on the state of the run and tests in it
    /// </summary>
    private _getRunOutcome(groupedResult: TCMContracts.TestResultsDetailsForGroup) {
        let runOutcome = -1;
        const currentStateOfRun: string = groupedResult.groupByValue.state;
        // If the logic is changed here, server side logic must be changed too. Both must be consistent.
        if (TestOutcomeEnum[TestOutcomeEnum.InProgress] === currentStateOfRun) {
            runOutcome = TestOutcomeEnum.InProgress;
        } else if (TestOutcomeEnum[TestOutcomeEnum.Aborted] === currentStateOfRun) {
            runOutcome = TestOutcomeEnum.Aborted;
        } else if (!(groupedResult.resultsCountByOutcome[TestOutcomeEnum.Failed] && groupedResult.resultsCountByOutcome[TestOutcomeEnum.Failed].count > 0) && groupedResult.resultsCountByOutcome[TestOutcomeEnum.Passed]) {
            runOutcome = TestOutcomeEnum.Passed;
        } else if (groupedResult.resultsCountByOutcome[TestOutcomeEnum.Failed]) {
            runOutcome = TestOutcomeEnum.Failed;
        } else if (groupedResult.resultsCountByOutcome[TestOutcomeEnum.NotImpacted]) {
            runOutcome = TestOutcomeEnum.NotImpacted;
        } else {
            runOutcome = TestOutcomeEnum.Others;
        }
        return runOutcome;
    }

    /// <summary>
    /// Returns details required for test run.
    /// </summary>
    private _getTestGroupDetails(
        groupedResult: TCMContracts.TestResultsDetailsForGroup,
        field: TestResultsGroupPivots,
        groupId: string
    ): ITestResultTreeData {
        let totalTests = 0;
        let totalMilliseconds = 0;
        let calculatedDuration = 0;
        const filteredTestsCount = groupedResult.results.length;

        const shouldCalculateDuration = this._groupByManager.shouldCalculateTotalDuration(field);

        for (const outcomeKey in groupedResult.resultsCountByOutcome) {
            const outcome = groupedResult.resultsCountByOutcome[outcomeKey];

            totalTests += outcome.count;
            if (shouldCalculateDuration) {
                totalMilliseconds += TestReportDataParser.getDurationInMilliseconds(outcome.duration);
            }
        }
        if (!!groupedResult.groupByValue.completedDate && !!groupedResult.groupByValue.startedDate){
            calculatedDuration = groupedResult.groupByValue.completedDate.getTime() - groupedResult.groupByValue.startedDate.getTime();
        }
        const groupByValue = GroupByPivotHelper.getPivotValueForGroupValue(groupedResult, field);
        if (calculatedDuration > 0)
        {
            totalMilliseconds = calculatedDuration;
        }
        // This will be runId when results are grouped by Test Run, otherwise it will be zero
        const runId: number = GroupByPivotHelper.getRunId(groupedResult, field);

        let runOutcome = -1;
        if (runId !== 0) {
            runOutcome = this._getRunOutcome(groupedResult);
        }

        const resultViewModel: ITestResultTreeData = {
            test: groupByValue,
            isTestCaseRow: false,
            duration: shouldCalculateDuration ? TCMContractsConverter.ConvertMilliSecondsToReadableFormatForResultSummary(totalMilliseconds) : "",
            storage: groupedResult.groupByValue.storage,
            expanded: false,
            groupId: groupId,
            nodeType: TreeNodeType.group,
            filteredTestsCount: filteredTestsCount,
            totalTestsCount: totalTests,
            runId: runId,
            runOutcome: runOutcome,
            durationInMs: totalMilliseconds,
            tags: groupedResult.tags
        };

        return resultViewModel;
    }

    private _getTreeItem: GetTreeItemFunction = (item: ITestResultTreeData) => {
        return {
            isExpandable: item.nodeType == TreeNodeType.group ? true : false,
            id: item.groupId ? item.groupId.toString() : "",
            defaultIsExpanded: item.expanded,
            item: item
        };
    };

    private _isTestUnreliable(customFields: TCMContracts.CustomTestField[]): boolean {
        if (customFields) {
            for (let index = 0; index < customFields.length; index++) {
                if (equals(customFields[index].fieldName, Constants.IsTestResultFlakyField, true)) {
                    return customFields[index].value === true;
                }

                if (equals(customFields[index].fieldName, Constants.OutcomeConfidenceField, true)) {
                    return parseFloat(customFields[index].value) === 0;
                }
            }
        }

        return false;
    }

    private _getFlakyState(customFields: TCMContracts.CustomTestField[]): TestResultFlakyState {
        let flakyState: TestResultFlakyState = TestResultFlakyState.None;
        if (customFields) {
            customFields.forEach(customField => {
                if (equals(customField.fieldName, Constants.TestResultFlakyStateField, true)) {
                    flakyState = Number(customField.value);
                }
            });
        }
        return flakyState;
    }

    private _handleShallowFetch() {
        if (!this._state.hasMegaFetchStarted && !this._isFilterOutcomeDefault()) {
            this._groupByManager.resetShallowFetchedDataInGroupByManager();
            this._filterManager.resetCache(true);
            this._state.fetchedResultsCount = 0;
            this._state.showingResultsCount = 0;
            this._state.hasMegaFetchStarted = true;
        }
    }

    private _handleRunLevelResultCap = () => {
        // Handle the primary scenario of run url sharing wherein we will not fetch the number of results indicated by the result summary
        this._state.totalResultsCount = this._state.showingResultsCount;
        if(!this._state.hasMegaFetchStarted){
            this._state.failedAbortedCount = this._state.showingResultsCount;
        }
        if (this._state.showingResultsCount == 0){
            this._state.resultsFound = false;
        }
    };

    private _hideLoadingWhenNoRestCallsMade = () =>
    {
        this._state.showLoading = false;
        this.emitChanged();
    }

    private _isFilterOutcomeDefault(): boolean {
        const filterState = this._state.filterState;
        if (filterState && filterState[FilterByFields.Outcome] && filterState[FilterByFields.Outcome].values.length == 2) {
            return filterState[FilterByFields.Outcome].values.every(
                filterValue =>
                    filterValue.toString().indexOf(TestResultsOutcomeFilterPivots.Filter_By_Failed) >= 0 ||
                    filterValue.toString().indexOf(TestResultsOutcomeFilterPivots.Filter_By_Aborted) >= 0
            );
        }

        return false;
    }
}

export interface ITestResultsState {
    columnToRender: string[];
    loading: boolean;
    totalResultsCount: number;
    failedAbortedCount: number;
    fetchedResultsCount: number;
    showingResultsCount: number;
    filterState: FilterState;
    groupByField: TestResultsGroupPivots;
    isInitialized: boolean;
    resultsFound: boolean;
    errorMessage?: string | null;
    warningMessage?: string | null;
    selectedTreeItems?: ITestResultTreeData[];
    endPerfMarker?: boolean;
    isSortedDescending: boolean;
    isFilteredOrGrouped: boolean;
    hasMegaFetchStarted: boolean;
    showLoading: boolean;
}
